import { useTranslation } from 'react-i18next'

import { useUserData } from '../../../../hooks'
import { getUserId } from '../../../../utils/user'
import {
  getBirdviewChats,
  getBirdviewFlats,
  getBirdviewPosts,
} from '../../DashboardBirdview.api'
import {
  BIRDVIEW_DWELLER_DOCUMENTS_SECTION_MOCK,
  BIRDVIEW_DWELLER_HOME_SECTION_MOCK,
} from '../../DashboardBirdview.mock'
import { sortBirdviewLoadItems } from '../../DashboardBirdview.utils'
import { toBirdviewListSection } from '../../DashboardBirdview.utils.api'
import { useBirdviewRequestLoaders } from '../useBirdviewRequestLoaders'

import {
  getBirdviewDwellerActiveFilters,
  getBirdviewDwellerChatsFilters,
  getBirdviewDwellerPostsFilters,
  getBirdviewFlatOpenRequestsFilters,
  getBirdviewFlatOverdueRequestsFilters,
  toBirdviewAnnouncementItem,
  toBirdviewDwellerRequestItem,
  toBirdviewFlatItem,
  toBirdviewMessageItem,
} from './useBirdviewDwellerLoaders.utils'

export function useBirdviewDwellerLoaders() {
  const { t } = useTranslation()

  const user = useUserData()

  const { getRequestsCountTile, getRequestsSection } =
    useBirdviewRequestLoaders()

  const userId = getUserId(user)

  async function getDwellerRequestsSection() {
    return getRequestsSection(
      getBirdviewDwellerActiveFilters(),
      toBirdviewDwellerRequestItem
    )
  }

  // TODO The posts filter answers with a pagination that has no `total`, so
  // the card shows neither the count badge nor "Show all" until it does.
  async function getDwellerAnnouncementsSection() {
    const { data } = await getBirdviewPosts(getBirdviewDwellerPostsFilters())

    return toBirdviewListSection(data, toBirdviewAnnouncementItem)
  }

  async function getDwellerMessagesSection() {
    const { data } = await getBirdviewChats(getBirdviewDwellerChatsFilters())

    return toBirdviewListSection(data, chat =>
      toBirdviewMessageItem(chat, userId)
    )
  }

  // TODO The files endpoint lists one folder at a time and a dweller's root
  // holds the system folders, not their files, so the card runs on the
  // prototype fixture until a recent-files endpoint exists.
  async function getDwellerDocumentsSection() {
    return BIRDVIEW_DWELLER_DOCUMENTS_SECTION_MOCK
  }

  // TODO The home card (address and management contacts) has no endpoint yet,
  // so it runs on the prototype fixture.
  async function getDwellerHomeSection() {
    return BIRDVIEW_DWELLER_HOME_SECTION_MOCK
  }

  async function getFlatLoad(flat) {
    const [{ count: open }, { count: overdue }] = await Promise.all([
      getRequestsCountTile(getBirdviewFlatOpenRequestsFilters(flat.id)),
      getRequestsCountTile(getBirdviewFlatOverdueRequestsFilters(flat.id)),
    ])

    return { ...flat, open, overdue }
  }

  // No per-flat aggregate exists, so the load of the shown objects is counted
  // one by one, as the properties card does for buildings.
  async function getDwellerObjectsSection() {
    const { data } = await getBirdviewFlats(getBirdviewDwellerActiveFilters())

    const items = await Promise.all(
      data.items.map(flat => toBirdviewFlatItem(flat, t)).map(getFlatLoad)
    )

    return {
      items: sortBirdviewLoadItems(items),
      total: data.pagination.total,
    }
  }

  return {
    getDwellerAnnouncementsSection,
    getDwellerDocumentsSection,
    getDwellerHomeSection,
    getDwellerMessagesSection,
    getDwellerObjectsSection,
    getDwellerRequestsSection,
  }
}
